import {
  ELiteral,
  TList,
  TElement,
  TTypeConstructor,
  TRecord,
  TText,
  TNumber,
} from '@packages/systems/wfdl-ast';
import {Plugins, Types, type Type} from '@packages/systems/wfdl';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {once} from '@packages/utilities/memo';
import {
  COLLECTION_LIST_PROPS,
  createCollectionListPropType,
  getCollectionListPropTypeNameString,
} from '@packages/systems/dynamo/utils/CollectionListPropNameUtils';
import type {PropertyListItemID} from '@packages/systems/ui/shared-ui/PropertyList';

const richTextChildrenTypeName = qualify(
  Plugins.name('Basic'),
  typeName('RichTextChildren')
);
const imageTypeName = qualify(Plugins.name('Asset'), typeName('Image'));
const linkTypeName = qualify(Plugins.name('Basic'), typeName('Link'));
export const LINK_DEFAULT_EXPRESSION = ELiteral({
  name: linkTypeName,
  value: {mode: 'external'},
});
const headingTagTypeName = qualify(
  Plugins.name('Basic'),
  typeName('HeadingTag')
);
const videoTypeName = qualify(Plugins.name('Embed'), typeName('Video'));
const codeTypeName = qualify(Plugins.name('Embed'), typeName('CodeProp'));
const visibilityTypeName = qualify(
  Plugins.name('Visibility'),
  typeName('VisibilityConditions')
);
const runtimePropsTypeName = qualify(
  Plugins.name('Devlink'),
  typeName('RuntimeProps')
);
const slotTypeName = qualify(Plugins.name('Devlink'), typeName('Slot'));
const triStateBooleanTypeName = qualify(
  Plugins.name('Basic'),
  typeName('TriStateBoolean')
);
const booleanTypeName = qualify(Plugins.name('Boolean'), typeName('Boolean'));
const variableModeTypeName = qualify(
  Plugins.name('Basic'),
  typeName('VariableMode')
);

type UserComponentProperty = {
  /**
   * The type of the prop
   */
  type: Type;
  /**
   * Determines whether a prop is suggestable
   */
  isSuggestable: boolean;
};

/**
 * These types are used to determine what Types of properties can be bound to components.
 * If one of these Types passes TypeChecking with a given field then a property
 * creation option is presented.
 */
export type UserComponentProperties = {
  Text: UserComponentProperty;
  Children: UserComponentProperty;
  RichTextChildren: UserComponentProperty;
  Image: UserComponentProperty;
  Link: UserComponentProperty;
  Video: UserComponentProperty;
  Code: UserComponentProperty;
  VisibilityConditions: UserComponentProperty;
  RuntimeProps: UserComponentProperty;
  Slot: UserComponentProperty;
  HeadingTag: UserComponentProperty;
  Number: UserComponentProperty;
  TriStateBoolean: UserComponentProperty;
  Boolean: UserComponentProperty;
  Attributes: UserComponentProperty;
  VariableMode: UserComponentProperty;
};

export const propertyTypes: UserComponentProperties = {
  Text: {type: TText, isSuggestable: false},
  Children: {type: TList(TElement), isSuggestable: true},
  RichTextChildren: {
    type: TTypeConstructor(richTextChildrenTypeName),
    isSuggestable: true,
  },
  Image: {type: TTypeConstructor(imageTypeName), isSuggestable: true},
  Link: {type: TTypeConstructor(linkTypeName), isSuggestable: true},
  Video: {type: TTypeConstructor(videoTypeName), isSuggestable: true},
  Code: {type: TTypeConstructor(codeTypeName), isSuggestable: false},
  VisibilityConditions: {
    type: TTypeConstructor(visibilityTypeName),
    isSuggestable: false,
  },
  RuntimeProps: {
    type: TTypeConstructor(runtimePropsTypeName),
    isSuggestable: false,
  },
  Slot: {
    type: TTypeConstructor(slotTypeName),
    isSuggestable: false,
  },
  HeadingTag: {
    type: TTypeConstructor(headingTagTypeName),
    isSuggestable: false,
  },
  Number: {type: TNumber, isSuggestable: false},
  TriStateBoolean: {
    type: TTypeConstructor(triStateBooleanTypeName),
    isSuggestable: false,
  },
  Boolean: {type: TTypeConstructor(booleanTypeName), isSuggestable: false},
  Attributes: {
    type: TTypeConstructor(Types.attributesTypeName),
    isSuggestable: false,
  },
  VariableMode: {
    type: TTypeConstructor(variableModeTypeName),
    isSuggestable: false,
  },
};

// Determines which properties are suggestable by looking at the `isSuggestable` property
export const getSuggestedPropertyTypes = once(() => {
  return Object.fromEntries(
    Object.entries(propertyTypes)
      .filter(([, value]) => value.isSuggestable)
      .map(([key, value]) => [key, value.type])
  );
});

const propertyTypeValues = Object.fromEntries(
  Object.entries(propertyTypes).map(([key, value]) => [key, value.type])
);

export const propertyTypesRecord = (collectionIds: string[]): Type => {
  const collectionListTypes = collectionIds.reduce<Record<string, Type>>(
    (acc, collectionId) => {
      for (const propKind of Object.values(COLLECTION_LIST_PROPS)) {
        acc[getCollectionListPropTypeNameString(propKind, collectionId)] =
          createCollectionListPropType(propKind, collectionId);
      }
      return acc;
    },
    {}
  );

  // Slot excluded — DevLink Slot is deprecated and no longer suggestable.
  const {Slot: _Slot, ...enabledTypes} = propertyTypeValues;

  return TRecord({
    ...enabledTypes,
    ...collectionListTypes,
  });
};

export type PropertyField = {
  icon: React.JSX.Element;
  id: PropertyListItemID;
  item: Type;
  label: string;
  isLinked: boolean;
  isDeletable: boolean;
  variantCount?: number;
  deprecated?: string;
};
